import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface SearchOption {
  value: string;
  label: string;
  /** A second line — a trade, a client, a reference. Searched as well. */
  hint?: string | null;
  /** Options with the same group are shown under one heading. */
  group?: string | null;
}

/**
 * A select you type into.
 *
 * A hundred and forty-six workers in a native dropdown is a scroll, not a
 * choice — the client asked for every long list to be typed at instead.
 * Filtering is on the label and the hint together, so "carpenter" finds the
 * carpenters and "T270" finds the tender.
 *
 * `allowCustom` keeps whatever is typed when nothing matches, for fields that
 * are a name rather than a record: the org chart holds seats filled by people
 * who may not have a login yet.
 */
export function SearchSelect({
  value,
  onChange,
  options,
  placeholder = 'Search…',
  emptyLabel = 'Nothing matches',
  allowCustom = false,
  clearable = true,
  disabled = false,
  id,
  className,
  'aria-label': ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  options: SearchOption[];
  placeholder?: string;
  emptyLabel?: string;
  allowCustom?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
}) {
  const generated = useId();
  const inputId = id ?? generated;
  const wrapper = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const selected = options.find((option) => option.value === value) ?? null;
  // A custom value is shown as itself; a chosen one by its label.
  const display = selected?.label ?? (allowCustom ? value : '');

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return options;

    return options.filter((option) => `${option.label} ${option.hint ?? ''}`.toLowerCase().includes(term));
  }, [options, query]);

  // Grouped in the order the groups first appear, so the caller decides it.
  const groups = useMemo(() => {
    const order: string[] = [];
    const byGroup = new Map<string, SearchOption[]>();

    for (const option of matches) {
      const key = option.group ?? '';
      if (!byGroup.has(key)) {
        byGroup.set(key, []);
        order.push(key);
      }
      byGroup.get(key)!.push(option);
    }

    return order.map((key) => ({ label: key, options: byGroup.get(key)! }));
  }, [matches]);

  /*
    Close on a click anywhere else, which is what a dropdown does — but keep
    what was typed on the way out.

    A mousedown outside lands *before* the input's blur. Clearing the query
    here therefore emptied it before `onBlur` could commit, so on a field that
    keeps what you type, typing a name and clicking away silently lost it. The
    org chart showed it worst: a seat's assignment went blank the moment you
    clicked the next field. Committing here as well means the name survives
    whichever way you leave the field.
  */
  useEffect(() => {
    if (!open) return;

    const close = (event: MouseEvent) => {
      if (wrapper.current?.contains(event.target as Node)) return;

      const typed = query.trim();
      if (allowCustom && typed && typed !== display) onChange(typed);

      setOpen(false);
      setQuery('');
    };

    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open, allowCustom, query, display, onChange]);

  useEffect(() => setActive(0), [query, open]);

  const choose = (option: SearchOption) => {
    onChange(option.value);
    setQuery('');
    setOpen(false);
  };

  const commitCustom = () => {
    if (!allowCustom) return;
    onChange(query.trim());
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) setOpen(true);
      setActive((current) => {
        const next = event.key === 'ArrowDown' ? current + 1 : current - 1;
        return Math.max(0, Math.min(matches.length - 1, next));
      });
      return;
    }

    if (event.key === 'Enter') {
      if (!open) return;
      event.preventDefault();
      const option = matches[active];
      if (option) choose(option);
      else commitCustom();
      return;
    }

    if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
      setQuery('');
    }
  };

  let index = -1;

  return (
    <div ref={wrapper} className={cn('relative', className)}>
      <div className="relative">
        <input
          ref={input}
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-label={ariaLabel}
          disabled={disabled}
          placeholder={selected || display ? undefined : placeholder}
          value={open ? query : display}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onBlur={() => {
            // Typing a name into a field that takes one keeps it.
            if (allowCustom && query.trim() && query.trim() !== display) commitCustom();
          }}
          onKeyDown={onKeyDown}
          className={cn(
            'h-10 w-full rounded-lg border border-line bg-white pl-3 pr-16 text-sm text-ink placeholder:text-muted',
            'focus:border-signal focus:outline-none focus:ring-2 focus:ring-signal-500/30',
            'disabled:cursor-not-allowed disabled:bg-cloud disabled:text-muted',
          )}
        />

        <span className="absolute inset-y-0 right-2 flex items-center gap-0.5">
          {clearable && (display || query) && !disabled && (
            <button
              type="button"
              aria-label="Clear"
              onClick={() => {
                onChange('');
                setQuery('');
                input.current?.focus();
              }}
              className="flex h-6 w-6 items-center justify-center rounded text-muted hover:bg-cloud hover:text-body"
            >
              <X aria-hidden className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            tabIndex={-1}
            aria-label={open ? 'Close the list' : 'Open the list'}
            disabled={disabled}
            onClick={() => {
              setOpen((current) => !current);
              input.current?.focus();
            }}
            className="flex h-6 w-6 items-center justify-center rounded text-muted hover:bg-cloud hover:text-body"
          >
            <ChevronDown aria-hidden className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
          </button>
        </span>
      </div>

      {open && (
        <ul
          role="listbox"
          className="absolute z-30 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-line bg-white py-1 shadow-raised scrollbar-slim"
        >
          {matches.length === 0 && (
            <li className="px-3 py-2.5 text-sm text-muted">
              {allowCustom && query.trim() ? `Use “${query.trim()}”` : emptyLabel}
            </li>
          )}

          {groups.map((group) => (
            <li key={group.label || 'ungrouped'}>
              {group.label && (
                <p className="px-3 pb-1 pt-2 text-2xs font-semibold uppercase tracking-wide text-muted">{group.label}</p>
              )}
              <ul>
                {group.options.map((option) => {
                  index += 1;
                  const isActive = index === active;
                  const isChosen = option.value === value;

                  return (
                    <li key={option.value}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isChosen}
                        onMouseEnter={() => setActive(matches.indexOf(option))}
                        onClick={() => choose(option)}
                        className={cn(
                          'flex w-full items-start gap-2 px-3 py-2 text-left text-sm',
                          isActive ? 'bg-cloud text-ink' : 'text-body hover:bg-cloud/60',
                        )}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{option.label}</span>
                          {option.hint && <span className="block truncate text-helper text-muted">{option.hint}</span>}
                        </span>
                        {isChosen && <Check aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-signal" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
